import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Shield, ArrowLeft, RefreshCw, CreditCard, Users, DollarSign,
  TrendingUp, AlertTriangle, CheckCircle, Clock, XCircle, Upload,
  Download, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStripe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pushingPlan, setPushingPlan] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [plans, setPlans] = useState([]);
  const [taxEnabled, setTaxEnabled] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stripe/subscriptions`, {
        headers: getAuthHeaders()
      });
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        console.error('Failed to fetch subscriptions:', error);
      }
    }
  }, [getAuthHeaders, navigate]);

  const fetchRevenue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stripe/revenue`, {
        headers: getAuthHeaders()
      });
      setRevenue(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
    }
  }, [getAuthHeaders]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      const plansData = response.data.plans || response.data || [];
      setPlans(plansData.filter(p => p.id !== 'free' && p.plan_id !== 'free'));
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  }, []);

  const fetchTaxStatus = useCallback(async () => {
    try {
      // Check if tax is enabled by looking at stripe_settings
      // For now we'll track it locally
    } catch (error) {
      console.error('Failed to fetch tax status:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSubscriptions(),
        fetchRevenue(),
        fetchPlans(),
        fetchTaxStatus()
      ]);
      setLoading(false);
    };

    loadData();
  }, [navigate, fetchSubscriptions, fetchRevenue, fetchPlans, fetchTaxStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/stripe/sync`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} sync errors occurred`);
      }
      await fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handlePushToStripe = async (planId, migrateExisting = false) => {
    setPushingPlan(planId);
    try {
      const response = await axios.post(
        `${API}/admin/plans/${planId}/push-to-stripe`,
        { migrate_existing: migrateExisting },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      if (response.data.migrated_subscribers > 0) {
        toast.info(`Migrated ${response.data.migrated_subscribers} subscribers to new price`);
      }
      await fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to Stripe');
    } finally {
      setPushingPlan(null);
    }
  };

  const handleEnableTax = async () => {
    try {
      await axios.post(`${API}/admin/stripe/enable-tax`, {}, {
        headers: getAuthHeaders()
      });
      setTaxEnabled(true);
      toast.success('Stripe Tax enabled for new checkouts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable tax');
    }
  };

  const handleDisableTax = async () => {
    try {
      await axios.post(`${API}/admin/stripe/disable-tax`, {}, {
        headers: getAuthHeaders()
      });
      setTaxEnabled(false);
      toast.success('Stripe Tax disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable tax');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      trialing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      past_due: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      canceled: 'bg-red-500/20 text-red-400 border-red-500/30',
      unpaid: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const icons = {
      active: <CheckCircle className="w-3 h-3" />,
      trialing: <Clock className="w-3 h-3" />,
      past_due: <AlertTriangle className="w-3 h-3" />,
      canceled: <XCircle className="w-3 h-3" />,
      unpaid: <AlertTriangle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || 'bg-slate-500/20 text-slate-400'}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading Stripe data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Stripe Management</h1>
                <p className="text-sm text-slate-400">Subscriptions, pricing, and billing</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Sync from Stripe
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6 space-y-6">
          {/* Revenue Stats */}
          {revenue && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Monthly Recurring</p>
                    <p className="text-2xl font-bold text-white">${revenue.mrr?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Revenue (30d)</p>
                    <p className="text-2xl font-bold text-white">${revenue.revenue_30d?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Active Subscribers</p>
                    <p className="text-2xl font-bold text-white">{revenue.subscription_counts?.active || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">On Trial</p>
                    <p className="text-2xl font-bold text-white">{revenue.subscription_counts?.trialing || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Push Pricing to Stripe */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-400" />
              Push Pricing to Stripe
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              When you change pricing in your Admin Plans page, push those changes to Stripe here. 
              New subscribers will get the new price. Optionally migrate existing subscribers.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plans.map(plan => {
                const planId = plan.id || plan.plan_id;
                const planName = plan.name || (planId === 'solo' ? 'Solo Pro' : planId === 'crew' ? 'Crew' : planId);
                return (
                  <div key={planId} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-white">{planName}</h3>
                        <p className="text-orange-400 font-bold">${plan.price}/mo</p>
                      </div>
                      {plan.stripe_price_id && (
                        <span className="text-xs text-slate-500">
                          {plan.stripe_price_id.substring(0, 20)}...
                        </span>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handlePushToStripe(planId, false)}
                        disabled={pushingPlan === planId}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                      >
                        {pushingPlan === planId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-1" />
                            Push Price
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm('This will change pricing for ALL existing subscribers on their next billing cycle. Continue?')) {
                            handlePushToStripe(planId, true);
                          }
                        }}
                        disabled={pushingPlan === planId}
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                      >
                        + Migrate All
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tax Settings */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Stripe Tax
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Enable automatic tax calculation for US customers. 
              <strong className="text-amber-400"> First, configure your head office address in Stripe Dashboard → Settings → Tax.</strong>
            </p>
            
            <div className="flex items-center gap-4">
              <Button
                onClick={handleEnableTax}
                disabled={taxEnabled}
                className={taxEnabled ? 'bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {taxEnabled ? 'Tax Enabled' : 'Enable Stripe Tax'}
              </Button>
              {taxEnabled && (
                <Button
                  variant="outline"
                  onClick={handleDisableTax}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                >
                  Disable Tax
                </Button>
              )}
            </div>
          </div>

          {/* Subscriptions List */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Active Subscriptions ({subscriptions.length})
            </h2>
            
            {subscriptions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No subscriptions yet</p>
                <p className="text-sm">Subscriptions will appear here when customers subscribe</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Customer</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Plan</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Period End</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map(sub => (
                      <tr key={sub.subscription_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">
                              {sub.local_user?.name || sub.customer_email || 'Unknown'}
                            </p>
                            <p className="text-slate-400 text-sm">{sub.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white">${sub.plan?.amount || 0}/mo</p>
                            <p className="text-slate-500 text-xs">{sub.plan?.price_id?.substring(0, 15)}...</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(sub.status)}
                          {sub.cancel_at_period_end && (
                            <p className="text-amber-400 text-xs mt-1">Cancels at period end</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-300 text-sm">
                          {new Date(sub.current_period_end).toLocaleDateString()}
                          {sub.trial_end && (
                            <p className="text-blue-400 text-xs">Trial ends: {new Date(sub.trial_end).toLocaleDateString()}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {sub.synced ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-400" title="Not synced to local DB" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
